/**
 * Cidades do jogo — 4 variações na MESMA grade de ruas (config.ROADS). O que
 * muda de uma cidade para outra:
 *   • lots        → posição/tipo/altura/rótulo dos PRÉDIOS (a variação principal)
 *   • parkCells   → quadras-parque (sem prédio, cheias de árvores)
 *   • roundabout  → quadra da rotatória/praça (fonte + placa WhatsApp). OPCIONAL:
 *                   uma cidade pode não ter praça (Mexican Town) — aí o destino
 *                   especial `plaza` simplesmente não existe naquele mapa.
 *   • cols / rows → tamanho da grade (padrão 5×3; Mexican Town é 4×4)
 *   • camera     → ENQUADRAMENTO de carregamento desta cidade: { position, target }.
 *                  OPCIONAL. Medido na mão na tela real (Config → desliga a câmera
 *                  dinâmica p/ liberar a câmera livre + liga o medidor de performance;
 *                  o bloco CÂMERA dá posição/alvo/distância/ângulos e copia no clique).
 *                  Define a vista do load E o TETO de zoom-out da câmera dinâmica —
 *                  ela nunca abre além daqui. Sem o campo, vale o enquadramento
 *                  histórico recuado pelo fator da grade (config.js).
 *   • lang        → IDIOMA do front nesta cidade (padrão 'pt'; Mexican é 'es').
 *                   Ver src/i18n.js — vale p/ HUD, telas e letreiros 3D.
 *   • entryRoads  / entryRoadsV → VIAS DE ACESSO (por onde os jogadores entram)
 *   • scoreGroup  → com QUEM esta cidade divide o PLACAR (pontos + missões do HUD).
 *                   As três cidades do funil (Acquisition · Engagement · Conversion)
 *                   usam o mesmo grupo `funnel`: pontuar em qualquer uma delas move o
 *                   MESMO número nos três telões. OPCIONAL — sem o campo, a cidade tem
 *                   placar próprio (é o caso da Mexican Town). Quem lê é o gateway
 *                   (server/index.js); o placar em si vive no banco (city_score).
 *
 * Cada cidade tem seus PRÓPRIOS `lots` — referências SEPARADAS de propósito: no
 * futuro vamos criar edifícios específicos por cidade (pode duplicar código).
 *
 * Grade: 3 linhas (r0..2) × 5 colunas (c0..4) — ou 4×4 com `cols: 4, rows: 4`. O
 * tamanho da QUADRA é o mesmo em todas (36×36 de terreno, prédio 18×18); o que
 * muda é quantas cabem, com as vias sempre no passo de 46u e recentradas. O
 * PRÉDIO ALTO mesclado + a via aparada variam por cidade (config.js resolve
 * `ROADS` conforme as flags):
 *   • Acquisition (padrão): bloco alto VERTICAL em {c4,r0-1}; via z=-23 aparada à
 *     direita (xMax:69).
 *   • Engagement (`flipX`): espelho X — bloco alto em {c0,r0-1}; z=-23 aparada à
 *     esquerda.
 *   • Conversion (`tallBlockHoriz`): bloco alto HORIZONTAL em {c2-c3,r0} (A3/A4);
 *     nenhuma horizontal aparada e a VERTICAL x=23 aparada na linha 0 (zMin=-23).
 *   • Mexican (`cols: 4, rows: 4`): 4×4; bloco alto VERTICAL na ÚLTIMA coluna
 *     {c3,r0-1}; a horizontal aparada à direita passa a ser z=-46 (xMax:46).
 *
 * Vias de acesso: via H pelas pontas left/right, via V por top/bottom — mas SÓ
 * onde a via existe (uma ponta aparada pelo prédio alto não aceita aquele lado).
 */

export const CITIES = [
  /* ----------------------------------------------------------------- */
  /*  Acquisition Town — a cidade original (inalterada).               */
  /* ----------------------------------------------------------------- */
  {
    id: 'acquisition',
    name: 'Acquisition Town',
    scoreGroup: 'funnel', // placar somado com Engagement e Conversion (ver o cabeçalho)
    // vista de carregamento medida na tela (as três cidades do funil compartilham o
    // MESMO enquadramento — mesma grade 5×3): mais ALTA e mais PERTO que a anterior
    // — dist 223.7 (era 247.9) e 39.6° a partir do zênite (era 48.7°), mesmo alvo
    camera: { position: [-0.5, 176.2, 160.8], target: [-0.5, 3.8, 18.2] },
    lots: [
      { c0: 0, c1: 0, r0: 0, r1: 0, type: 'coworking', label: 'COWORKING', h: 18 }, // A1 — coworking (lâmpada-ideia + terraço-lounge no teto; o café foi p/ o C3)
      { c0: 1, c1: 1, r0: 0, r1: 0, type: 'travel', label: 'AGÊNCIA DE\nVIAGENS', h: 14 }, // A2 — agência de viagens (globo+avião no teto; era office "INADIMPLÊNCIA")
      { c0: 3, c1: 3, r0: 1, r1: 1, type: 'research', label: 'LABORATÓRIO', h: 13 }, // B4 — lab de PESQUISA (trocou com o resort; era A3)
      { c0: 4, c1: 4, r0: 0, r1: 1, type: 'shopping', label: 'SHOPPING', h: 15 }, // A5/B5 (mall: corpo baixo + abóbada de vidro)
      { c0: 1, c1: 1, r0: 1, r1: 1, type: 'kinder', label: 'ESCOLA\nINFANTIL', h: 14 }, // B2 — escola infantil (blocos A·B·C no teto; era a fintech)
      { c0: 2, c1: 2, r0: 0, r1: 0, type: 'resort', label: 'HOTEL', h: 18 }, // A3 — Hotel Resort (trocou com o lab de pesquisa)
      { c0: 0, c1: 0, r0: 2, r1: 2, type: 'spa', label: 'SPA', h: 13 }, // C1 — Spa/bem-estar (varanda com fonte; era a loja de eletrônicos)
      { c0: 2, c1: 2, r0: 2, r1: 2, type: 'cafe', label: '', h: 11 }, // C3 — café (veio do A1; a escola institucional foi aposentada do mapa)
      { c0: 3, c1: 3, r0: 2, r1: 2, type: 'boutique', label: 'BOUTIQUE', h: 12 }, // C4 — boutique moderna (vitrines de manequim térreo + sacadas 2º piso + terraço-jardim/caramanchão no teto; a farmácia foi aposentada do mapa)
      { c0: 4, c1: 4, r0: 2, r1: 2, type: 'electronics', label: 'ELETRÔNICOS', h: 13 }, // C5 — loja de eletrônicos (veio do C1)
      { c0: 1, c1: 1, r0: 2, r1: 2, type: 'pizza', label: 'PIZZARIA', h: 13 }, // C2 — pizzaria (fatia gigante no teto; a floricultura foi aposentada do mapa)
      { c0: 3, c1: 3, r0: 0, r1: 0, type: 'fintech', label: 'FINTECH', h: 20 }, // A4 — fintech/banco (veio do B4)
      { c0: 0, c1: 0, r0: 1, r1: 1, type: 'library', label: 'BIBLIOTECA', h: 13 } // B1 — biblioteca (pilha de livros + coruja no teto; era a clínica de estética)
    ],
    parkCells: [],
    roundabout: { c: 2, r: 1 }, // B3 — praça/rotatória (veio do C2)
    entryRoads: [
      { z: -23, sides: ['left'] },
      { z: 23, sides: ['left', 'right'] }
    ],
    // as 4 verticais INTERNAS entram pelas DUAS pontas (antes cada uma tinha só um
    // lado, alternado): dobra as bocas de entrada e deixa topo e base simétricos
    entryRoadsV: [
      { x: -69, sides: ['top', 'bottom'] },
      { x: -23, sides: ['top', 'bottom'] },
      { x: 23, sides: ['top', 'bottom'] },
      { x: 69, sides: ['top', 'bottom'] }
    ]
  },

  /* ----------------------------------------------------------------- */
  /*  Engagement Town — ESPELHO HORIZONTAL da Acquisition (flip no X):  */
  /*  lots e entradas espelhados (coluna c → 4-c) + `flipX` espelha as  */
  /*  vias horizontais no config.js. Variação em relação à Acquisition. */
  /* ----------------------------------------------------------------- */
  {
    id: 'engagement',
    name: 'Engagement Town',
    scoreGroup: 'funnel', // placar somado com Acquisition e Conversion
    camera: { position: [-0.5, 176.2, 160.8], target: [-0.5, 3.8, 18.2] }, // = Acquisition (mesma grade 5×3)
    flipX: true, // config.js espelha a via z=-23 (aparada) p/ a esquerda
    lots: [
      { c0: 0, c1: 0, r0: 0, r1: 1, type: 'shopping', label: 'SHOPPING', h: 15 }, // A1/B1 (prédio alto — agora à ESQUERDA)
      { c0: 1, c1: 1, r0: 0, r1: 0, type: 'fintech', label: 'FINTECH', h: 20 }, // A2
      { c0: 2, c1: 2, r0: 0, r1: 0, type: 'resort', label: 'HOTEL', h: 18 }, // A3
      { c0: 3, c1: 3, r0: 0, r1: 0, type: 'travel', label: 'AGÊNCIA DE\nVIAGENS', h: 14 }, // A4
      { c0: 4, c1: 4, r0: 0, r1: 0, type: 'coworking', label: 'COWORKING', h: 18 }, // A5
      { c0: 1, c1: 1, r0: 1, r1: 1, type: 'research', label: 'LABORATÓRIO', h: 13 }, // B2
      { c0: 3, c1: 3, r0: 1, r1: 1, type: 'kinder', label: 'ESCOLA\nINFANTIL', h: 14 }, // B4
      { c0: 4, c1: 4, r0: 1, r1: 1, type: 'library', label: 'BIBLIOTECA', h: 13 }, // B5
      { c0: 0, c1: 0, r0: 2, r1: 2, type: 'electronics', label: 'ELETRÔNICOS', h: 13 }, // C1
      { c0: 1, c1: 1, r0: 2, r1: 2, type: 'boutique', label: 'BOUTIQUE', h: 12 }, // C2
      { c0: 2, c1: 2, r0: 2, r1: 2, type: 'cafe', label: '', h: 11 }, // C3
      { c0: 3, c1: 3, r0: 2, r1: 2, type: 'pizza', label: 'PIZZARIA', h: 13 }, // C4
      { c0: 4, c1: 4, r0: 2, r1: 2, type: 'spa', label: 'SPA', h: 13 } // C5
    ],
    parkCells: [],
    roundabout: { c: 2, r: 1 }, // B3 — praça/rotatória (coluna central: espelho não move)
    entryRoads: [
      { z: -23, sides: ['right'] }, // espelhado: prédio alto agora à esquerda → só entra pela direita
      { z: 23, sides: ['left', 'right'] }
    ],
    // as 4 verticais INTERNAS entram pelas DUAS pontas, igual à Acquisition (as vias
    // verticais não são espelhadas pelo `flipX` — são simétricas —, então vale o mesmo)
    entryRoadsV: [
      { x: -69, sides: ['top', 'bottom'] },
      { x: -23, sides: ['top', 'bottom'] },
      { x: 23, sides: ['top', 'bottom'] },
      { x: 69, sides: ['top', 'bottom'] }
    ]
  },

  /* ----------------------------------------------------------------- */
  /*  Conversion Town — arranjo CUSTOM do usuário. Shopping mesclado      */
  /*  HORIZONTAL em A3/A4 (`tallBlockHoriz` + `rot90` no shopping); a      */
  /*  ROTATÓRIA/praça fica no C3 (não no B3 — o Hotel ocupa o B3).        */
  /* ----------------------------------------------------------------- */
  {
    id: 'conversion',
    name: 'Conversion Town',
    scoreGroup: 'funnel', // placar somado com Acquisition e Engagement
    camera: { position: [-0.5, 176.2, 160.8], target: [-0.5, 3.8, 18.2] }, // = Acquisition (mesma grade 5×3)
    tallBlockHoriz: true, // config.js: bloco alto horizontal (apara x=23 na linha 0)
    lots: [
      // Linha A: coworking · banco · SHOPPING (mesclado A3/A4 horizontal) · hotel
      { c0: 0, c1: 0, r0: 0, r1: 0, type: 'coworking', label: 'COWORKING', h: 18 }, // A1
      { c0: 1, c1: 1, r0: 0, r1: 0, type: 'fintech', label: 'FINTECH', h: 20 }, // A2 (banco)
      { c0: 2, c1: 3, r0: 0, r1: 0, type: 'shopping', label: 'SHOPPING', h: 15, rot90: true }, // A3/A4 — MESMO modelo do bloco vertical, girado 90°
      { c0: 4, c1: 4, r0: 0, r1: 0, type: 'resort', label: 'HOTEL', h: 18 }, // A5 (hotel — trocou com o café do B3)
      // Linha B: agência · pizzaria · café · eletrônicos · laboratório
      { c0: 0, c1: 0, r0: 1, r1: 1, type: 'travel', label: 'AGÊNCIA DE\nVIAGENS', h: 14 }, // B1
      { c0: 1, c1: 1, r0: 1, r1: 1, type: 'pizza', label: 'PIZZARIA', h: 13 }, // B2
      { c0: 2, c1: 2, r0: 1, r1: 1, type: 'cafe', label: '', h: 11 }, // B3 (café — trocou com o hotel do A5)
      { c0: 3, c1: 3, r0: 1, r1: 1, type: 'electronics', label: 'ELETRÔNICOS', h: 13 }, // B4
      { c0: 4, c1: 4, r0: 1, r1: 1, type: 'research', label: 'LABORATÓRIO', h: 13 }, // B5
      // Linha C: boutique · spa · [PRAÇA C3] · escola infantil · biblioteca
      { c0: 0, c1: 0, r0: 2, r1: 2, type: 'boutique', label: 'BOUTIQUE', h: 12 }, // C1
      { c0: 1, c1: 1, r0: 2, r1: 2, type: 'spa', label: 'SPA', h: 13 }, // C2
      { c0: 3, c1: 3, r0: 2, r1: 2, type: 'kinder', label: 'ESCOLA\nINFANTIL', h: 14 }, // C4
      { c0: 4, c1: 4, r0: 2, r1: 2, type: 'library', label: 'BIBLIOTECA', h: 13 } // C5
    ],
    parkCells: [],
    roundabout: { c: 2, r: 2 }, // C3 — praça/rotatória (movida do B3; o "Parque" do usuário)
    entryRoads: [
      { z: -23, sides: ['left', 'right'] }, // inteiras (não há prédio alto na coluna)
      { z: 23, sides: ['left', 'right'] }
    ],
    // as verticais internas entram pelas DUAS pontas, como nas outras cidades — salvo
    // x=23, aparada no topo pelo mall horizontal A3/A4: lá ela não chega à borda
    entryRoadsV: [
      { x: -69, sides: ['top', 'bottom'] },
      { x: -23, sides: ['top', 'bottom'] },
      { x: 23, sides: ['bottom'] },
      { x: 69, sides: ['top', 'bottom'] }
    ]
  },

  /* ----------------------------------------------------------------- */
  /*  Mexican Town — grade 4×4 (16 quadras), a única fora do 5×3. Nasceu */
  /*  como a Acquisition SEM a coluna do meio (saíram hotel A3, PRAÇA B3 */
  /*  e café C3) e depois ganhou uma via horizontal a mais → a LINHA D.  */
  /*  16 quadras = 15 prédios (o shopping ocupa 2). SEM praça/rotatória: */
  /*  `roundabout` ausente → este mapa não tem o destino especial        */
  /*  `plaza`. Os prédios da linha D são PROVISÓRIOS (2 tipos repetidos, */
  /*  marcados abaixo) — vão todos ser trocados.                         */
  /* ----------------------------------------------------------------- */
  {
    id: 'mexican',
    name: 'Mexican Town',
    lang: 'es', // toda a comunicação do front nesta cidade sai em espanhol (i18n.js)
    // sem `scoreGroup`: a Mexican Town corre POR FORA do funil — placar (pontos e
    // missões) só dela, sem somar com as outras três nem receber o que elas fazem.
    cols: 6, // config.js: grade 6×4 (GRID_6X4_ROADS) — cada distrito é uma superquadra 2×3
    rows: 4,
    // vista medida na tela p/ ESTA grade (4×4 é mais funda que a 5×3, então a câmera
    // fica mais alta e mais longe que a do funil: dist 264.6 contra 223.7). Mesma
    // inclinação das outras (39.6° do zênite) — o que muda é só o afastamento.
    // Substitui o recuo automático por GRID_FIT que valia antes (dist 324.9): a
    // medição manual enquadra melhor do que a régua proporcional.
    camera: { position: [-0.3, 194.2, 166.3], target: [-0.3, -9.7, 6.7] }, // medida na tela p/ a grade 6×4 empacotada (GRID_FIT 1.149; dist 258.9, polar 38.0°, azim 0.0°)
    // Lotes AGRUPADOS POR DISTRITO — é o distrito que dá sentido ao arranjo. Repetir
    // tipo aqui é DELIBERADO (o banco 4×, a cafeteria 2×): estes prédios são
    // provisórios e vão ser trocados por modelos próprios da cidade.
    lots: [
      // ---- DISTRITO 1 (financeiro) — TROCADO p/ a DIREITA (A4·A5·A6 · B4·B5·B6) ----
      { c0: 3, c1: 3, r0: 1, r1: 1, type: 'mint', label: 'CASA DA MOEDA', h: 16.5 }, // B4 — Casa da Moeda
      { c0: 4, c1: 4, r0: 0, r1: 0, type: 'fintech', label: 'FINTECH', h: 20 }, // A5 — banco
      { c0: 5, c1: 5, r0: 0, r1: 0, type: 'insurance', label: 'SEGURADORA', h: 20 }, // A6 — seguradora
      { c0: 3, c1: 3, r0: 0, r1: 0, type: 'bullion', label: 'BANCO DE OURO', h: 15 }, // A4 — Banco de Ouro e Prata (bimetal; 3 pav.+friso)
      { c0: 4, c1: 4, r0: 1, r1: 1, type: 'park', label: '', h: 0 }, // B5 — PARQUE (quadra inteira)
      { c0: 5, c1: 5, r0: 1, r1: 1, type: 'stocks', label: 'BOLSA DE VALORES', h: 16.5 }, // B6 — bolsa
      // ---- DISTRITO 2 (CPG/lazer) — TROCADO p/ a ESQUERDA (A1·A2·A3 · B1·B2·B3) ----
      { c0: 0, c1: 0, r0: 0, r1: 0, type: 'spa', label: 'SPA', h: 13 }, // A1 — spa
      { c0: 1, c1: 1, r0: 0, r1: 0, type: 'cafe', label: '', h: 11 }, // A2 — cafeteria
      { c0: 2, c1: 2, r0: 0, r1: 0, type: 'beauty', label: 'ESTÉTICA', h: 13 }, // A3 — Estética/Cosméticos (CPG beleza); era a 2ª cópia do spa
      { c0: 0, c1: 0, r0: 1, r1: 1, type: 'cereals', label: 'CEREAIS & SNACKS', h: 13 }, // B1 — supermercado (cereais)
      { c0: 1, c1: 1, r0: 1, r1: 1, type: 'park', label: '', h: 0 }, // B2 — PARQUE
      { c0: 2, c1: 2, r0: 1, r1: 1, type: 'vitamins', label: 'VITAMINAS', h: 13 }, // B3 — vitaminas
      // ---- DISTRITO 3 (C1·C2·C3 · D1·D2·D3): serviços ----
      { c0: 0, c1: 0, r0: 2, r1: 2, type: 'resort', label: 'HOTEL', h: 18 }, // C1 — Hotel (era automóveis)
      { c0: 1, c1: 1, r0: 2, r1: 2, type: 'travel', label: 'AGÊNCIA DE\nVIAGENS', h: 14 }, // C2 — viagens
      { c0: 2, c1: 2, r0: 2, r1: 2, type: 'kinder', label: 'ESCOLA\nINFANTIL', h: 14 }, // C3 — escola infantil
      { c0: 0, c1: 0, r0: 3, r1: 3, type: 'autos', label: 'AUTOMÓVEIS', h: 11 }, // D1 — cópia dos automóveis
      { c0: 1, c1: 1, r0: 3, r1: 3, type: 'park', label: '', h: 0 }, // D2 — PARQUE
      { c0: 2, c1: 2, r0: 3, r1: 3, type: 'realestate', label: 'IMOBILIÁRIA', h: 14 }, // D3 — imobiliária
      // ---- DISTRITO 4 (C4·C5·C6 · D4·D5·D6): comércio ----
      { c0: 3, c1: 4, r0: 2, r1: 2, type: 'shopping', label: 'SHOPPING', h: 15, rot90: true }, // C4/C5 — SHOPPING horizontal (mesclado)
      { c0: 5, c1: 5, r0: 2, r1: 2, type: 'ecommerce', label: 'E-COMMERCE', h: 13 }, // C6 — E-commerce (logística + loja online); substituiu a pizzaria
      { c0: 3, c1: 3, r0: 3, r1: 3, type: 'boutique', label: 'BOUTIQUE', h: 12 }, // D4 — boutique
      { c0: 4, c1: 4, r0: 3, r1: 3, type: 'park', label: '', h: 0 }, // D5 — PARQUE
      { c0: 5, c1: 5, r0: 3, r1: 3, type: 'electronics', label: 'ELETRÔNICOS', h: 13 } // D6 — eletrônicos (movida p/ a nova coluna direita)
    ],
    // DISTRITOS: os 4 quadrantes 2×2 da grade. Cada um é uma SUPERQUADRA EMPACOTADA de 96×64
    // (3×2 células de 32, canteiros colados) — as linhas internas (x=∓37,∓69 e z=∓37) viram
    // calçada/miolo só de pedestre; sobram como via o anel externo (x/z=±106/±74) e a cruz
    // central (x=0/z=0), que une os quatro. Ver cityLayout.DISTRICTS.
    // `color` = cor TEMÁTICA do distrito. Hoje ela aparece só na FONTE do jardim
    // central (água + bulbo + halo — ver Decor.makeFountain); a arquitetura
    // e o piso continuam neutros de propósito.
    districts: [
      { id: 1, c0: 3, c1: 5, r0: 0, r1: 1, color: '#20a6df' }, // Finance — TROCADO p/ a DIREITA (A4·A5·A6·B4·B5·B6)
      { id: 2, c0: 0, c1: 2, r0: 0, r1: 1, color: '#ffbc38' }, // CPG — TROCADO p/ a ESQUERDA (A1·A2·A3·B1·B2·B3)
      { id: 3, c0: 0, c1: 2, r0: 2, r1: 3, color: '#5e47de' }, // C1·C2·C3·D1·D2·D3 (2×3)
      { id: 4, c0: 3, c1: 5, r0: 2, r1: 3, color: '#fa6533' } // C4·C5·C6·D4·D5·D6 (2×3)
    ],
    parkCells: [],
    // sem `roundabout`: Mexican Town não tem praça (a quadra dela saiu junto com a
    // coluna do meio). Para devolvê-la, basta apontar uma célula livre aqui.
    // Vias de acesso: UMA por lado, na CRUZ CÍVICA (x=0 e z=0). Com a superquadra só
    // restam 3 ruas por eixo (anel externo ±92 e o centro), e as do anel encostam na
    // QUINA da cidade — a via de acesso saía raspando o canto em vez de entrar pelo
    // meio. As ±46 não servem: viraram calçada (config.isDrivable). Sobra o centro, que
    // é onde a entrada lê como avenida chegando à cidade.
    // QUATRO BOCAS, uma por lado — e só nos eixos que ATRAVESSAM a cidade.
    //
    // Nas vias ±37 o eixo vira PARQUE dentro da superquadra (ver addEntryStubs em
    // config.js: lá o acesso encosta no anel por um toco de 10u). Quem entrava por uma
    // dessas bocas chegava de frente para o calçadão e tinha de virar no anel logo na
    // chegada — foi o que o usuário viu como carro "batendo de frente com a calçada".
    // Os eixos x=0 e z=0 correm na folga entre as quatro superquadras e são as únicas
    // vias contínuas de ponta a ponta: quem entra por elas segue reto para dentro da
    // cidade sem manobra na boca.
    entryRoads: [{ z: 0, sides: ['left', 'right'] }],
    entryRoadsV: [{ x: 0, sides: ['top', 'bottom'] }]
  }
];

/** Cidade pelo id (cai na primeira se não achar). */
export function getCity(id) {
  return CITIES.find((c) => c.id === id) || CITIES[0];
}

/** Id da cidade ativa: ?city= na URL → localStorage → primeira (Acquisition). */
export function activeCityId() {
  try {
    const url = new URLSearchParams(location.search).get('city');
    if (url && CITIES.some((c) => c.id === url)) return url;
    const ls = localStorage.getItem('wabs.city');
    if (ls && CITIES.some((c) => c.id === ls)) return ls;
  } catch (e) {
    /* sem window/localStorage (SSR/teste) — usa o padrão */
  }
  return CITIES[0].id;
}

/** Persiste a escolha e recarrega para reconstruir a cidade selecionada. */
export function selectCity(id) {
  try {
    localStorage.setItem('wabs.city', id);
  } catch (e) {
    /* ignora */
  }
  location.reload();
}

/** Config da cidade ativa, resolvida uma vez no carregamento. */
export const ACTIVE_CITY = getCity(activeCityId());
